import * as Cesium from 'cesium';
import { DATASET_PATH } from './config';
import { createViewer } from './cesium/createViewer';
import { renderGeoJson } from './cesium/renderGeoJson';
import { installInteraction } from './cesium/interaction';
import { GeoJsonLoadError, loadGeoJson } from './geojson/loadGeoJson';
import { diagnoseFeatureCollection } from './geojson/diagnostics';
import { normalizeFeatureCollection } from './geojson/normalize';
import { trimThinOverlaps } from './geojson/trimOverlaps';
import { formatDevSummary, formatDevRepairSummary } from './geojson/report';
import { createLoading } from './ui/loading';
import { createErrorPanel } from './ui/errorPanel';
import { createAboutPanel } from './ui/aboutPanel';
import {
  createKnowledgePanel,
  type KnowledgePanelController,
} from './ui/knowledgePanel';
import { createYearSelector } from './ui/yearSelector';
import { createTooltip } from './ui/tooltip';
import { loadYear } from './geojson/loadYear';

/**
 * Mount point #root hosts three children:
 *   - the Cesium canvas (created by createViewer)
 *   - the loading overlay (created by createLoading)
 *   - the error overlay (created by createErrorPanel)
 *   - the tooltip (created by createTooltip)
 *   - the knowledge panel (created by createKnowledgePanel)
 *   - a small attribution credit (created here)
 *
 * Order matters for visual stacking: attribution is appended last so it is
 * above the canvas but below the error overlay (the error overlay is itself
 * appended after the tooltip, so it sits on the very top).
 */
export function mountApp(root: HTMLElement): void {
  const loading = createLoading(root);
  const errorPanel = createErrorPanel(root);
  createAboutPanel(root);
  rendering(root, loading, errorPanel);
}

function rendering(
  root: HTMLElement,
  loading: ReturnType<typeof createLoading>,
  errorPanel: ReturnType<typeof createErrorPanel>,
): void {
  let viewer: Cesium.Viewer;
  try {
    viewer = createViewer(root);
  } catch (createError) {
    loading.hide();
    errorPanel.show(
      'Could not start the 3D globe.',
      describeError(createError),
    );
    return;
  }

  const tooltip = createTooltip(root);
  const knowledgePanel = createKnowledgePanel(root);
  // Diagnostic seam: lets a user open the panel from DevTools without a
  // click, separating rendering issues from input issues.
  (
    window as unknown as { __historyAtlasPanel?: KnowledgePanelController }
  ).__historyAtlasPanel = knowledgePanel;
  installInteraction(viewer, tooltip, {
    onHoverName(name) {
      // Hover name is surfaced via the tooltip element only in M1.
      void name;
    },
    onClick(name) {
      // Polygon click → entity → knowledge layer → side panel (M5).
      void knowledgePanel.openForName(name);
    },
  });

  mountAttribution(root);

  // Year selector
  const yearSelector = createYearSelector(root, {
    onYearChange(_year: number, file: string) {
      loading.setText('Loading historical data…');
      loadYear(file)
        .then((collection) => {
          // Remove old data source
          const oldDs = viewer.dataSources.get(0);
          if (oldDs) viewer.dataSources.remove(oldDs, true);
          // Remove old labels
          viewer.entities.removeAll();
          // Render new data
          renderGeoJson(viewer, collection).then(() => {
            loading.hide();
          });
        })
        .catch(() => {
          loading.setText('Failed to load historical data.');
        });
    },
  });

  // Fetch years.json and populate selector
  fetch('/data/years.json')
    .then((res) => res.json())
    .then((entries) => {
      yearSelector.setYears(entries);
      const defaultEntry =
        entries.find((e: { year: number }) => e.year === 100) ?? entries[0];
      if (defaultEntry) {
        yearSelector.setCurrentYear(defaultEntry.year);
      }
    });

  loading.setText('Loading the historical globe…');
  void loadDataset(viewer, loading, errorPanel);
}

async function loadDataset(
  viewer: Cesium.Viewer,
  loading: ReturnType<typeof createLoading>,
  errorPanel: ReturnType<typeof createErrorPanel>,
): Promise<void> {
  try {
    const result = await loadGeoJson(DATASET_PATH);
    loading.setText(`Loaded ${result.featureCount} features, normalising…`);

    // Diagnostics + normalisation both run in dev so the developer sees
    // before/after summaries and the repair log. In production we still run
    // normalisation (the renderer needs it to suppress the artifact) but
    // skip the dev-only console.info blocks.
    if (import.meta.env.DEV) {
      const preReport = diagnoseFeatureCollection(result.collection);
      console.info(formatDevSummary(preReport.summary));
    }

    const normalized = normalizeFeatureCollection(result.collection);
    const trimmed = trimThinOverlaps(normalized.collection);
    normalized.collection = trimmed.collection;
    if (import.meta.env.DEV && trimmed.entries.length > 0) {
      console.info(
        `[history-atlas] trimmed ${trimmed.entries.length} thin overlaps`,
      );
    }

    if (import.meta.env.DEV) {
      console.info(formatDevRepairSummary(normalized.report));
      const postReport = diagnoseFeatureCollection(normalized.collection);
      console.info('[history-atlas] post-normalisation diagnostics:');
      console.info(formatDevSummary(postReport.summary));
    }

    loading.setText(
      `Drawing ${normalized.collection.features.length} features…`,
    );
    const dataSource = await renderGeoJson(viewer, normalized.collection);
    loading.hide();

    // Start over North America, then fly to the user's geolocation.
    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(-100, 40, 15_000_000),
    });

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(
              pos.coords.longitude,
              pos.coords.latitude,
              9_000_000,
            ),
            duration: 3,
          });
        },
        () => {
          // Geolocation denied/unavailable — fly to dataset overview instead
          viewer.flyTo(dataSource);
        },
        { timeout: 5000 },
      );
    } else {
      viewer.flyTo(dataSource);
    }
  } catch (loadError) {
    loading.hide();
    const message =
      loadError instanceof GeoJsonLoadError
        ? 'The historical dataset failed to load.'
        : 'The globe failed to render the loaded dataset.';
    errorPanel.show(message, describeError(loadError));
  }
}

function mountAttribution(root: HTMLElement): void {
  const credit = document.createElement('div');
  credit.className = 'attribution';
  credit.innerHTML = [
    '<strong>History Atlas</strong> — History has coordinates.',
    ' <img src="/cesiumjs-logomark.svg" alt="CesiumJS" style="height:14px;vertical-align:middle;display:inline-block;margin:0 2px;"> ',
    'Powered by <a href="https://cesium.com/platform/cesiumjs/" target="_blank" rel="noopener">CesiumJS</a>.',
    ' Historical basemap: world, 100 CE.',
    ' Data source and license: see About.',
  ].join(' ');
  root.append(credit);
}

function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.name === error.message
      ? error.name
      : `${error.name}: ${error.message}`;
  }
  return String(error);
}
